import { useCallback } from 'react';

export const useTableSelection = ({ data, columns, updateContext, unitIndex, selectedCells = [] }) => {
    const isCellEditable = (colName) => {
    const colDef = columns.find(c => c.name === colName);
    if (!colDef) return false;
    return (
      colName !== "row_id" &&         // Block row_id updates
      colDef.name !== "seq" &&        // Block sequence column
      colDef.type !== "button" &&     // Block button types
      colDef.locked !== true          // Block explicitly locked columns
    );
  };
  // --- 1. MOUSE CLICK HANDLER (Single & Shift + Click) ---
  const handleCellClick = useCallback((rowId, colName, e) => {
    // Check for existing selection to act as anchor

  const isAlreadySelected = selectedCells.some(
    c => c.rowId === rowId && c.colName === colName
  );
    if (e && e.shiftKey && selectedCells.length > 0) {
      const anchor = selectedCells[0]; 
      
      const anchorRowIdx = data.findIndex(r => r.row_id === anchor.rowId);
      const anchorColIdx = columns.findIndex(c => c.name === anchor.colName);
      const targetRowIdx = data.findIndex(r => r.row_id === rowId);
      const targetColIdx = columns.findIndex(c => c.name === colName);

      if (anchorRowIdx !== -1 && targetRowIdx !== -1) {
        const startRow = Math.min(anchorRowIdx, targetRowIdx);
        const endRow = Math.max(anchorRowIdx, targetRowIdx);
        const startCol = Math.min(anchorColIdx, targetColIdx);
        const endCol = Math.max(anchorColIdx, targetColIdx);

        const newSelection = [];
        for (let r = startRow; r <= endRow; r++) {
          for (let c = startCol; c <= endCol; c++) {
            newSelection.push({ rowId: data[r].row_id, colName: columns[c].name });
          }
        }
        updateContext(`context_units.${unitIndex}.view.selectedCells`, newSelection);
        return;
      }
    }
     if (isAlreadySelected) return;

    // Normal Click (Reset to single cell)
    updateContext(`context_units.${unitIndex}.view.selectedCells`, [{ rowId, colName }]);
  }, [data, columns, selectedCells, unitIndex, updateContext]);
  // inside useTableSelection hook
// Inside useTableSelection hook
const handleBulkFillAction = useCallback((value) => {
  if (!selectedCells || selectedCells.length === 0) return;

  // Use a single "Batch" approach for better performance
  selectedCells.forEach(cell => {
    if (!isCellEditable(cell.colName)) return; 
    const colDef = columns.find(c => c.name === cell.colName);
    const rowIndex = data.findIndex(r => r.row_id === cell.rowId);

    if (rowIndex !== -1 && colDef) {
      const path = `context_units.${unitIndex}.data.${rowIndex}.row.${cell.colName}`;
      
      let finalValue = value;
      if (colDef.type === "number") {
        const parsed = parseFloat(value);
        finalValue = isNaN(parsed) ? 0 : parsed;
      }
      
      updateContext(path, finalValue);
    }
  });
}, [data, columns, selectedCells, unitIndex, updateContext]);


  const handleBulkReplace = useCallback((newValue) => {
    if (!selectedCells.length) return;

    selectedCells.forEach(cell => {
      if (!isCellEditable(cell.colName)) return; 
      const colDef = columns.find(c => c.name === cell.colName);
      const rowIndex = data.findIndex(r => r.row_id === cell.rowId);

      if (rowIndex !== -1 && colDef) {
        const path = `context_units.${unitIndex}.data.${rowIndex}.row.${cell.colName}`;
        
        let finalValue = newValue;

        // --- Health Check for Numbers ---
        if (colDef.type === "number") {
          // If input is empty, set to 0. Otherwise try to parse.
          const parsed = parseFloat(newValue);
          finalValue = isNaN(parsed) ? 0 : parsed;
        }

        updateContext(path, finalValue);
      }
    });
  }, [data, columns, selectedCells, unitIndex, updateContext]);

  const handleSmartPaste = useCallback((clipboardText) => {
  if (!selectedCells.length || !clipboardText) return;

  // 1. Split the text into a 2D array (Rows and Columns)
  const rows = clipboardText.split(/\r?\n/).filter(row => row.length > 0);
  const pasteData = rows.map(row => row.split('\t'));

  // 2. Find the top-left corner of your purple selection
  const minRowIdx = Math.min(...selectedCells.map(c => data.findIndex(r => r.row_id === c.rowId)));
  const minColIdx = Math.min(...selectedCells.map(c => columns.findIndex(col => col.name === c.colName)));

  // 3. Map the clipboard data onto the table
  pasteData.forEach((rowData, rOffset) => {
    rowData.forEach((cellValue, cOffset) => {
      const targetRow = data[minRowIdx + rOffset];
      const targetCol = columns[minColIdx + cOffset];

      if (targetRow && targetCol) {
        const path = `context_units.${unitIndex}.data.${minRowIdx + rOffset}.row.${targetCol.name}`;
        
        // Validation for numbers
        let finalValue = cellValue.trim();
        if (targetCol.type === "number") {
          finalValue = parseFloat(finalValue.replace(/,/g, '')) || 0;
        }

        updateContext(path, finalValue);
      }
    });
  });
}, [data, columns, selectedCells, unitIndex, updateContext]);


  // --- 2. KEYBOARD HANDLER (Arrows & Shift + Arrows) ---
  const handleTableKeyDown = useCallback((e) => {
    if (!data.length || !selectedCells.length) return;

    const anchor = selectedCells[0];
    const focus = selectedCells[selectedCells.length - 1];

    const rowIndex = data.findIndex(r => r.row_id === focus.rowId);
    const colIndex = columns.findIndex(c => c.name === focus.colName);

    if (rowIndex === -1 || colIndex === -1) return;

      // --- NEW DELETE LOGIC ---
  if (e.key === "Delete" || e.key === "Backspace") {
    // If the user is currently "Deep Editing" (cursor is inside a textbox), 
    // we let the textbox handle its own backspacing. 
    // We only trigger BULK DELETE if the focus is on the table/TD level.
    const activeTag = document.activeElement.tagName;
    if (activeTag === 'INPUT' || activeTag === 'TEXTAREA') return;

    e.preventDefault(); // Stop page from jumping

    console.log(`%c🗑️ Bulk Deleting ${selectedCells.length} cells...`, "color: #ff4757; font-weight: bold;");

    // 3. Iterate through every cell in the purple selection
    selectedCells.forEach(cell => {
      if (!isCellEditable(cell.colName)) return; 
      const colDef = columns.find(c => c.name === cell.colName);
      
      // Find the specific row in the master data
      const rowIndex = data.findIndex(r => r.row_id === cell.rowId);

      if (rowIndex !== -1 && colDef) {
        // Construct the path to this specific cell's value in the Context
        const path = `context_units.${unitIndex}.data.${rowIndex}.row.${cell.colName}`;
        
        let resetValue = ""; // Default for text

        // Check Column Type for specialized resets
        if (colDef.type === "number") {
          resetValue = 0; 
        } else if (colDef.type === "select") {
          // Reset to first option value or empty string
          resetValue = colDef.options?.[0]?.value || "";
        }

        // Apply the update to this specific cell
        updateContext(path, resetValue);
      }
    });

    return; // Exit after processing delete
  }

    const keys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    if (!keys.includes(e.key)) return;

    e.preventDefault();

    let nextRow = rowIndex;
    let nextCol = colIndex;

    if (e.key === "ArrowUp") nextRow = Math.max(0, rowIndex - 1);
    if (e.key === "ArrowDown") nextRow = Math.min(data.length - 1, rowIndex + 1);
    if (e.key === "ArrowLeft") nextCol = Math.max(0, colIndex - 1);
    if (e.key === "ArrowRight") nextCol = Math.min(columns.length - 1, colIndex + 1);

    if (e.shiftKey) {
      const anchorRowIdx = data.findIndex(r => r.row_id === anchor.rowId);
      const anchorColIdx = columns.findIndex(c => c.name === anchor.colName);

      const startRow = Math.min(anchorRowIdx, nextRow);
      const endRow = Math.max(anchorRowIdx, nextRow);
      const startCol = Math.min(anchorColIdx, nextCol);
      const endCol = Math.max(anchorColIdx, nextCol);

      const newSelection = [];
      for (let r = startRow; r <= endRow; r++) {
        for (let c = startCol; c <= endCol; c++) {
          newSelection.push({ rowId: data[r].row_id, colName: columns[c].name });
        }
      }
      updateContext(`context_units.${unitIndex}.view.selectedCells`, newSelection);
    } else {
      updateContext(`context_units.${unitIndex}.view.selectedCells`, [
        { rowId: data[nextRow].row_id, colName: columns[nextCol].name }
      ]);
    }
  }, [data, columns, selectedCells, unitIndex, updateContext]);

  return { handleCellClick, handleTableKeyDown,handleBulkReplace,handleBulkFillAction,handleSmartPaste };
};

// // Instead of looping inside the hook, prepare one big update object
// const bulkDataUpdate = {};
// selectedCells.forEach(cell => {
//   // ... prepare values ...
//   bulkDataUpdate[path] = finalValue;
// });
// // Call a single specialized context update if your Engine supports it
// updateContextBatch(bulkDataUpdate); 

